import type { ValidateErrorEntity } from '@rc-component/form/lib/interface';
import { Form as FormAD, type FormProps } from 'antd';
import cn from 'classnames';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFormInstance, useWatch } from './hooks';
import { Item } from './item';
import { ResetFormOnHistoryChange } from './reset-form-on-history-change';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
type FormP<Values = any> = {
  children?: ReactNode;
  noOptional?: boolean;
  resetOnHistoryChange?: boolean;
} & FormProps<Values>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
const Form = <Values = any,>({
  children,
  className,
  noOptional,
  onFinishFailed,
  resetOnHistoryChange,
  ...props
}: FormP<Values>) => {
  // TODO this is a hack to focus on first error field, waiting for answer from feature proposal https://github.com/ant-design/ant-design/issues/45701
  const internalFinishFailed = (errorInfo: ValidateErrorEntity<Values>) => {
    const errorField = errorInfo.errorFields[0];
    const fieldKey = errorField?.name[0];

    const input = document.querySelector<HTMLInputElement>(`[id="${fieldKey}"]`);

    if (input && typeof input.focus === 'function') {
      input.focus();
    }

    onFinishFailed?.(errorInfo);
  };

  const { t } = useTranslation();

  /* eslint-disable no-template-curly-in-string -- Antd interface inserts template strings */
  const validateMessages = {
    required: t('Error: ${label} is required'),
    string: { max: 'Error: ${label} cannot be longer than ${max} characters' },
    types: {
      email: t('Error: E-mail is not valid. Please enter a valid email address (e.g., example@email.com)'),
      number: t('Error: ${label} is not a valid number'),
    },
    whitespace: t('Error: ${label} cannot be a blank character'),
  };
  /* eslint-enable no-template-curly-in-string -- Antd interface inserts template strings */

  return (
    <FormAD<Values>
      className={cn(className, 'ui-form', { 'ui-form--no-optional': noOptional })}
      layout="vertical"
      onFinishFailed={internalFinishFailed}
      requiredMark={false}
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      validateMessages={validateMessages}
      {...props}
    >
      {resetOnHistoryChange ? <ResetFormOnHistoryChange /> : null}
      {children}
    </FormAD>
  );
};

Form.useForm = useForm;
Form.useFormInstance = useFormInstance;
Form.useWatch = useWatch;

Form.List = FormAD.List;
Form.Item = Item;

export { type FormInstance, type FormListFieldData } from 'antd/lib/form';
export default Form;
